import { useState, useEffect } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { Copy } from "lucide-react"
import { toast } from "sonner"

import {
  AuthCard,
  AuthPageHeader,
  authButtonClass,
  authOutlineButtonClass,
} from "@/components/auth/AuthCard"
import { AuthLayout } from "@/components/layouts/AuthLayout"
import { Button } from "@/components/ui/button"
import { FullPageLoader } from "@/components/ui/full-page-loader"
import { useAuth } from "@/contexts/AuthContext"
import { cn } from "@/lib/utils"

export function ShareStorePage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, isLoading, isAuthenticated, refreshUser } = useAuth()
  const [storeUrl, setStoreUrl] = useState<string>("")
  const [storeLogo, setStoreLogo] = useState<string>("")
  const [isRefreshing, setIsRefreshing] = useState(false)

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate("/login")
      return
    }

    if (
      !isLoading &&
      user &&
      user.onboardingStatus === "pending" &&
      !isRefreshing
    ) {
      setIsRefreshing(true)
      refreshUser().finally(() => {
        setIsRefreshing(false)
      })
      return
    }

    if (location.state?.storeUrl) {
      setStoreUrl(location.state.storeUrl)
    } else {
      navigate("/dashboard")
    }

    if (location.state?.storeLogo) {
      setStoreLogo(location.state.storeLogo)
    }
  }, [
    location,
    navigate,
    isLoading,
    isAuthenticated,
    user,
    refreshUser,
    isRefreshing,
  ])

  if (isLoading || isRefreshing) {
    return <FullPageLoader text="Loading your store" variant="page" />
  }

  const copyToClipboard = () => {
    navigator.clipboard.writeText(storeUrl)
    toast.success("Store URL copied to clipboard!")
  }

  const shareOnWhatsapp = () => {
    const text = `Check out my store: ${storeUrl}`
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank")
  }

  const shareOnFacebook = () => {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(storeUrl)}`,
      "_blank",
    )
  }

  const shareOnInstagram = () => {
    navigator.clipboard.writeText(storeUrl)
    toast.success("URL copied! Open Instagram and paste in your story or post")
  }

  const shareOnTwitter = () => {
    const text = `Check out my store: ${storeUrl}`
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`,
      "_blank",
    )
  }

  const handleContinue = async () => {
    try {
      await refreshUser()
      setTimeout(() => {
        navigate("/dashboard")
      }, 100)
    } catch (error) {
      console.error("Error refreshing user data:", error)
      navigate("/dashboard")
    }
  }

  const shareButtonClass = cn(
    authOutlineButtonClass,
    "justify-between px-4 hover:bg-[#f6f4ef]",
  )

  return (
    <AuthLayout>
      <AuthCard>
        <div className="mb-8 text-center">
          {storeLogo ? (
            <div className="mx-auto mb-8 flex size-24 items-center justify-center overflow-hidden rounded-[1.75rem] border border-primary/10 bg-[#faf9f7]">
              <img
                src={storeLogo}
                alt="Store Logo"
                className="size-full object-contain p-2"
              />
            </div>
          ) : (
            <div className="mx-auto mb-8 flex size-16 items-center justify-center rounded-2xl bg-[#d4c4fc]/50">
              <span role="img" aria-label="celebration" className="text-3xl">
                🎉
              </span>
            </div>
          )}

          <AuthPageHeader
            eyebrow="You're live"
            title="Your website is ready to share"
            description="Make your store visible on your social media and website"
            align="center"
            showLogo={false}
            className="mb-0"
          />
        </div>

        <div className="mb-6 flex w-full items-center rounded-2xl border border-primary/15 bg-[#faf9f7] p-4">
          <span className="flex-1 truncate text-sm font-semibold text-primary">
            {storeUrl}
          </span>
          <Button
            variant="ghost"
            size="icon"
            onClick={copyToClipboard}
            className="ml-2 rounded-xl hover:bg-white"
          >
            <Copy className="h-4 w-4" />
          </Button>
        </div>

        <div className="mb-8 w-full space-y-3">
          <Button
            onClick={shareOnWhatsapp}
            className={shareButtonClass}
            variant="outline"
          >
            <div className="flex items-center">
              <div className="mr-3 flex size-8 items-center justify-center rounded-full bg-[#25D366] text-white">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="white"
                >
                  <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
                </svg>
              </div>
              <span>Share on Whatsapp</span>
            </div>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7 17L17 7M17 7H7M17 7V17"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Button>

          <Button
            onClick={shareOnFacebook}
            className={shareButtonClass}
            variant="outline"
          >
            <div className="flex items-center">
              <div className="mr-3 flex size-8 items-center justify-center rounded-full bg-[#1877F2] text-white">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="white"
                >
                  <path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z" />
                </svg>
              </div>
              <span>Share on Facebook</span>
            </div>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7 17L17 7M17 7H7M17 7V17"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Button>

          <Button
            onClick={shareOnInstagram}
            className={shareButtonClass}
            variant="outline"
          >
            <div className="flex items-center">
              <div className="mr-3 flex size-8 items-center justify-center rounded-full bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#FCAF45] text-white">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="white"
                >
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
              </div>
              <span>Share on Instagram</span>
            </div>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7 17L17 7M17 7H7M17 7V17"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Button>

          <Button
            onClick={shareOnTwitter}
            className={shareButtonClass}
            variant="outline"
          >
            <div className="flex items-center">
              <div className="mr-3 flex size-8 items-center justify-center rounded-full bg-black text-white">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="white"
                >
                  <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z" />
                </svg>
              </div>
              <span>Share on Twitter</span>
            </div>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7 17L17 7M17 7H7M17 7V17"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Button>
        </div>

        <Button onClick={handleContinue} className={authButtonClass}>
          Continue
        </Button>
      </AuthCard>
    </AuthLayout>
  )
}
